import { Disclosure } from "@headlessui/react";
import { Fragment } from "react";
import { Icon } from "../../../../../core/components/atoms/Icon";
import { Typography } from "../../../../../core/components/molecules/Typography";
import { useFilterConfigStore } from "../../../stores/filterConfig";
import InnerCheckbox from "../../exercises/components/innerCheckbox";

interface ISectionProps {
  compact?: boolean;
}

const getTitleSize = (compact: boolean) => (compact ? 'h5' : 'h4');
const getSectionButtonClass = (compact: boolean) =>
  `flex w-full items-center justify-between text-left ${compact ? 'py-1' : ''}`;
const getSectionPanelClass = (compact: boolean) =>
  `${compact ? 'mt-2 gap-md pb-2 pl-3' : 'mt-5 gap-xl pb-3 pl-4'} flex w-full flex-col`;
const getInlinePanelClass = (compact: boolean) =>
  `${compact ? 'gap-md pb-2 pl-3' : 'gap-xl pb-3 pl-4'} flex w-full flex-col`;

const TypeFilter: React.FC<ISectionProps> = ({ compact = false }) => {
  const { type, setType } = useFilterConfigStore();

  return (
    <Disclosure>
      {({ open }) => (
        <Fragment>
          <Disclosure.Button className={getSectionButtonClass(compact)}>
            <Typography size={getTitleSize(compact)} type='title' className='text-left'>
              Tipo
            </Typography>
            <Icon
              Icon='expand_more'
              type='material'
              className={`${open ? 'rotate-180 transform' : ''} h-5 w-5 text-mono-color-black`}
            />
          </Disclosure.Button>
          <Disclosure.Panel className={getInlinePanelClass(compact)}>
            <InnerCheckbox
              onChange={() => setType('main')}
              label='Principais'
              checked={type.includes('main')}
              id='type-principais'
              value='main'
            />
            <InnerCheckbox
              onChange={() => setType('summary')}
              label='Resumo'
              checked={type.includes('summary')}
              id='type-resumos'
              value='summary'
            />
          </Disclosure.Panel>
        </Fragment>
      )}
    </Disclosure>
  );
};

const StatusFilterLesson: React.FC<ISectionProps> = ({ compact = false }) => {
  const { status, setStatus } = useFilterConfigStore();

  return (
    <Disclosure>
      {({ open }) => (
        <Fragment>
          <Disclosure.Button className={getSectionButtonClass(compact)}>
            <Typography size={getTitleSize(compact)} type='title' className='text-left'>
              Status
            </Typography>
            <Icon
              Icon='expand_more'
              type='material'
              className={`${open ? 'rotate-180 transform' : ''} h-5 w-5 text-mono-color-black`}
            />
          </Disclosure.Button>
          <Disclosure.Panel className={getInlinePanelClass(compact)}>
            <InnerCheckbox
              onChange={() => setStatus('not_played')}
              label='Não assistidos'
              checked={status.includes('not_played')}
              id='status-not-played'
              value='not_played'
            />
            <InnerCheckbox
              onChange={() => setStatus('played')}
              label='Assistidos'
              checked={status.includes('played')}
              id='status-played'
              value='played'
            />
          </Disclosure.Panel>
        </Fragment>
      )}
    </Disclosure>
  );
};

const StatusFilterMaterial: React.FC<ISectionProps> = ({ compact = false }) => {
  const { status, setStatus } = useFilterConfigStore();

  return (
    <Disclosure>
      {({ open }) => (
        <Fragment>
          <Disclosure.Button className={getSectionButtonClass(compact)}>
            <Typography size={getTitleSize(compact)} type='title' className='text-left'>
              Status
            </Typography>
            <Icon
              Icon='expand_more'
              type='material'
              className={`${open ? 'rotate-180 transform' : ''} h-5 w-5 text-mono-color-black`}
            />
          </Disclosure.Button>
          <Disclosure.Panel className={getSectionPanelClass(compact)}>
            <InnerCheckbox
              onChange={() => setStatus('not_seen')}
              label='Não visualizados'
              checked={status.includes('not_seen')}
              id='status-not-seen'
              value='not_seen'
            />
            <InnerCheckbox
              onChange={() => setStatus('seen')}
              label='Visualizados'
              checked={status.includes('seen')}
              id='status-seen'
              value='seen'
            />
          </Disclosure.Panel>
        </Fragment>
      )}
    </Disclosure>
  );
};

const StatusFilterPodcast: React.FC<ISectionProps> = ({ compact = false }) => {
  const { status, setStatus } = useFilterConfigStore();

  return (
    <Disclosure>
      {({ open }) => (
        <Fragment>
          <Disclosure.Button className={getSectionButtonClass(compact)}>
            <Typography size={getTitleSize(compact)} type='title' className='text-left'>
              Status
            </Typography>
            <Icon
              Icon='expand_more'
              type='material'
              className={`${open ? 'rotate-180 transform' : ''} h-5 w-5 text-mono-color-black`}
            />
          </Disclosure.Button>
          <Disclosure.Panel className={getSectionPanelClass(compact)}>
            <InnerCheckbox
              onChange={() => setStatus('not_listened')}
              label='Não reproduzidos'
              checked={status.includes('not_listened')}
              id='status-not-listened'
              value='not_listened'
            />
            <InnerCheckbox
              onChange={() => setStatus('listened')}
              label='Reproduzidos'
              checked={status.includes('listened')}
              id='status-listened'
              value='listened'
            />
          </Disclosure.Panel>
        </Fragment>
      )}
    </Disclosure>
  );
};

const IncidenceFilter: React.FC<ISectionProps> = ({ compact = false }) => {
  const { incidence, setIncidence } = useFilterConfigStore();

  return (
    <Disclosure>
      {({ open }) => (
        <Fragment>
          <Disclosure.Button className={getSectionButtonClass(compact)}>
            <Typography size={getTitleSize(compact)} type='title' className='text-left'>
              Incidência
            </Typography>
            <Icon
              Icon='expand_more'
              type='material'
              className={`${open ? 'rotate-180 transform' : ''} h-5 w-5 text-mono-color-black`}
            />
          </Disclosure.Button>
          <Disclosure.Panel className={getSectionPanelClass(compact)}>
            {Array.from({ length: 5 }, (_, index) => (
              <InnerCheckbox
                key={index}
                onChange={() => setIncidence(index + 1)}
                label={`Incidência ${index + 1}`}
                checked={incidence.includes(index + 1)}
                id={`incidence-${index + 1}`}
                value={`${index + 1}`}
              />
            ))}
          </Disclosure.Panel>
        </Fragment>
      )}
    </Disclosure>
  );
};

interface IAnotherContentFilter {
  contentType: string | number;
  compact?: boolean;
}

const AnotherContentFilter: React.FC<IAnotherContentFilter> = ({ contentType, compact = false }) => {
  const { favorites, setFavorites } = useFilterConfigStore();

  return (
    <div className={`flex w-full flex-col ${compact ? 'gap-sm' : 'gap-md'}`}>
      <InnerCheckbox
        onChange={() => setFavorites(!favorites)}
        label='Favoritos'
        checked={favorites}
        id='filter-favorites'
        value='favorites'
      />
      {(contentType !== 'podcast' && contentType !== 3) && <TypeFilter compact={compact} />}
      {(contentType === 'videoaula' || contentType === 0) && <StatusFilterLesson compact={compact} />}
      {(contentType === 'videoaula' || contentType === 0) && <IncidenceFilter compact={compact} />}
      {(contentType === 'materiais' || contentType === 2) && <StatusFilterMaterial compact={compact} />}
      {(contentType === 'podcast' || contentType === 3) && <StatusFilterPodcast compact={compact} />}
    </div>
  );
};

export default AnotherContentFilter;
